import { Controller } from "@hotwired/stimulus"
import {
  PERMISSION_STATUS,
  getStoredPermission, setStoredPermission,
  getDismissedData, setDismissedData,
  getDenialDate, setDenialDate,
  clearDenialData,
  isRegisteredThisSession, markAsRegisteredThisSession,
  clearRegistrationFlag, clearAllStorage
} from "../services/onesignal_storage"

/**
 * OneSignal Device Controller
 *
 * Handles OneSignal device registration for push notifications.
 *
 * Flow:
 * 1. Mobile only (Flutter InAppWebView)
 * 2. Try automatic enrollment if user never refused
 * 3. If user refuses permissions → store date, wait 5 days
 * 4. After 5 days → show modal ONCE with commercial message
 * 5. "Autoriser" → request native permission, register device, clear refusal
 * 6. "Peut-être plus tard" → close modal, restart 5-day countdown
 *
 * ============================================================
 * CRITICAL: OneSignal Push Lifecycle Rules
 * ============================================================
 *
 * 1. NEVER call optOutPush on logout
 * 2. optOutPush is ONLY allowed when user deletes account or explicitly disables notifications
 * 3. Logout must ONLY detach the user (externalUserId = null) and clear session flag
 * 4. Login must re-attach user, re-opt-in if needed, and re-register device
 * 5. JS storage is UX-only — OneSignal SDK on Flutter maintains its own state
 */
export default class extends Controller {
  static targets = ["modal", "allowButton", "laterButton"]
  static values = {
    apiUrl: { type: String, default: "/api/v1/devices" },
    daysToHide: { type: Number, default: 5 }
  }

  static MAX_WAIT_ATTEMPTS = 20
  static WAIT_INTERVAL_MS = 250

  get isInApp() {
    return typeof window.flutter_inappwebview !== "undefined"
  }

  connect() {
    if (isRegisteredThisSession() || !this.isInApp) {
      this.hideModal()
      return
    }
    this.waitForFlutter()
  }

  waitForFlutter(attempts = 0) {
    if (this.isInApp) {
      this.initialize()
      return
    }
    if (attempts >= this.constructor.MAX_WAIT_ATTEMPTS) {
      // Bridge jamais injecté (navigateur classique ou WebView sans handler)
      this.hideModal()
      return
    }
    setTimeout(() => this.waitForFlutter(attempts + 1), this.constructor.WAIT_INTERVAL_MS)
  }

  async initialize() {
    const oneSignalInfo = await this.getOneSignalInfo()
    await this.handleLoginLifecycle(oneSignalInfo)

    const permissionStatus = getStoredPermission()

    if (permissionStatus === PERMISSION_STATUS.granted) {
      await this.registerDeviceAutomatically()
      this.hideModal()
      return
    }

    if (permissionStatus === PERMISSION_STATUS.denied) {
      this.shouldShowModalAfterDenial() ? this.showModal() : this.hideModal()
      return
    }

    await this.attemptAutomaticEnrollment()
  }

  // ─── Login Lifecycle ───────────────────────────────────────────────

  async handleLoginLifecycle(oneSignalInfo) {
    if (!this.isInApp) return

    try {
      await this.attachPushUser()

      if (oneSignalInfo) {
        const hasPermission = oneSignalInfo.has_permission === true
        const isOptedIn = oneSignalInfo.opted_in === true
        if (hasPermission && !isOptedIn) {
          console.log("[OneSignal] Re-opting in: has_permission=true, opted_in=false")
          await this.optInPush()
        }
      }
    } catch (error) {
      console.error("[OneSignal] Login lifecycle handling failed:", error)
    }
  }

  // ─── Enrollment ────────────────────────────────────────────────────

  async attemptAutomaticEnrollment() {
    try {
      const permissionResult = await this.requestPushPermission()

      if (!permissionResult.granted) {
        setStoredPermission(PERMISSION_STATUS.denied)
        setDenialDate()
        this.hideModal()
        return
      }

      await new Promise(resolve => setTimeout(resolve, 500))
      const oneSignalInfo = await this.getOneSignalInfo()

      if (!oneSignalInfo || !oneSignalInfo.player_id) {
        this.hideModal()
        return
      }

      setStoredPermission(PERMISSION_STATUS.granted)
      await this.registerDevice(oneSignalInfo)
      markAsRegisteredThisSession()
      clearDenialData()
      this.hideModal()
    } catch (error) {
      console.error("[OneSignal] Automatic enrollment failed:", error)
      setStoredPermission(PERMISSION_STATUS.denied)
      setDenialDate()
      this.hideModal()
    }
  }

  async registerDeviceAutomatically() {
    try {
      const oneSignalInfo = await this.getOneSignalInfo()
      if (!oneSignalInfo || !oneSignalInfo.player_id) return

      if (oneSignalInfo.has_permission === false) {
        setStoredPermission(PERMISSION_STATUS.denied)
        setDenialDate()
        return
      }

      if (oneSignalInfo.has_permission === true && oneSignalInfo.opted_in === false) {
        console.log("[OneSignal] Auto-registration: re-opting in user")
        await this.optInPush()
        const updatedInfo = await this.getOneSignalInfo()
        if (updatedInfo && updatedInfo.player_id) {
          await this.registerDevice(updatedInfo)
          markAsRegisteredThisSession()
        }
      } else {
        await this.registerDevice(oneSignalInfo)
        markAsRegisteredThisSession()
      }
    } catch (error) {
      console.error("[OneSignal] Auto registration failed:", error)
    }
  }

  shouldShowModalAfterDenial() {
    const dismissedData = getDismissedData()

    if (!dismissedData) {
      const denialDate = getDenialDate()
      if (!denialDate) return true
      const daysSince = Math.floor((Date.now() - new Date(denialDate).getTime()) / (1000 * 60 * 60 * 24))
      return daysSince >= this.daysToHideValue
    }

    const daysSince = Math.floor((Date.now() - new Date(dismissedData.date).getTime()) / (1000 * 60 * 60 * 24))
    return daysSince >= this.daysToHideValue
  }

  // ─── Flutter/OneSignal Communication ───────────────────────────────

  async getOneSignalInfo() {
    if (!this.isInApp) return null
    try {
      return await window.flutter_inappwebview.callHandler("getOneSignalInfo")
    } catch (error) {
      console.error("[OneSignal] Failed to get OneSignal info:", error)
      return null
    }
  }

  async requestPushPermission() {
    if (!this.isInApp) return {granted: false}
    try {
      return (await window.flutter_inappwebview.callHandler("requestPushPermission")) || {granted: false}
    } catch (error) {
      console.error("[OneSignal] Failed to request permission:", error)
      return { granted: false }
    }
  }

  async attachPushUser() {
    if (!this.isInApp) return
    try {
      await window.flutter_inappwebview.callHandler("attachPushUser")
    } catch (error) {
      console.error("[OneSignal] Failed to attach push user:", error)
    }
  }

  async optInPush() {
    if (!this.isInApp) return
    try {
      await window.flutter_inappwebview.callHandler("optInPush")
    } catch (error) {
      console.error("[OneSignal] Failed to opt in:", error)
    }
  }

  async registerDevice(oneSignalInfo) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const response = await fetch(this.apiUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json"
      },
      body: JSON.stringify({
        player_id: oneSignalInfo.player_id,
        push_token: oneSignalInfo.push_token,
        has_permission: oneSignalInfo.has_permission,
        opted_in: oneSignalInfo.opted_in,
        os: oneSignalInfo.os,
        os_version: oneSignalInfo.os_version,
        brand: oneSignalInfo.brand
      }),
      credentials: "same-origin"
    })

    if (!response.ok) throw new Error(`Device registration failed: ${response.status}`)
  }

  // ─── Modal Actions ─────────────────────────────────────────────────

  showModal() {
    if (this.hasModalTarget) this.modalTarget.classList.remove("hidden")
  }

  hideModal() {
    if (this.hasModalTarget) this.modalTarget.classList.add("hidden")
  }

  async allowNotifications(event) {
    event.preventDefault()
    this.hideModal()

    try {
      const permissionResult = await this.requestPushPermission()

      if (permissionResult.granted) {
        setStoredPermission(PERMISSION_STATUS.granted)
        clearDenialData()
        await new Promise(resolve => setTimeout(resolve, 500))

        const oneSignalInfo = await this.getOneSignalInfo()
        if (oneSignalInfo && oneSignalInfo.player_id) {
          await this.registerDevice(oneSignalInfo)
          markAsRegisteredThisSession()
        }
      } else {
        setStoredPermission(PERMISSION_STATUS.denied)
        setDenialDate()
        setDismissedData()
      }
    } catch (error) {
      console.error("[OneSignal] Permission request failed:", error)
      setStoredPermission(PERMISSION_STATUS.denied)
      setDenialDate()
    }
  }

  dismissLater(event) {
    event.preventDefault()
    setDismissedData()
    this.hideModal()
  }
}

// ============================================================
// Exported Helper Functions
// ============================================================

/**
 * Detach user from OneSignal on logout (SAFE for logout)
 *
 * CRITICAL: This does NOT call optOutPush.
 * optOutPush permanently disables push — use only for account deletion.
 */
export async function detachPushUserOnLogout() {
  clearRegistrationFlag()

  if (typeof window.flutter_inappwebview !== "undefined") {
    try {
      await window.flutter_inappwebview.callHandler("detachPushUser")
    } catch (error) {
      console.error("[OneSignal] Failed to detach push user:", error)
    }
  }
}

/**
 * Full opt-out and cleanup for ACCOUNT DELETION ONLY
 *
 * NEVER call this on normal logout — use detachPushUserOnLogout() instead.
 */
export async function optOutAndClearDeviceForDeletion() {
  clearAllStorage()

  if (typeof window.flutter_inappwebview !== "undefined") {
    try {
      await window.flutter_inappwebview.callHandler("optOutPush")
    } catch (error) {
      console.error("[OneSignal] Failed to opt out:", error)
    }
  }
}

/**
 * @deprecated Use detachPushUserOnLogout() for logout
 * @deprecated Use optOutAndClearDeviceForDeletion() for account deletion
 */
export async function optOutAndClearDevice() {
  console.warn(
    "[OneSignal] DEPRECATED: optOutAndClearDevice() is deprecated. " +
    "Use detachPushUserOnLogout() for logout or optOutAndClearDeviceForDeletion() for account deletion."
  )
  await detachPushUserOnLogout()
}
